import { useEffect } from 'react'
import { useRoomContext } from '@livekit/components-react'
import {
  RoomEvent,
  Track,
  type ElementInfo,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteVideoTrack,
} from 'livekit-client'

import { report } from './diagnostics'
import { shareRequestSize } from './screenShare'

/**
 * Keeps a remote screen share out of adaptive stream's pause path — and gets it
 * back if it stalls anyway.
 *
 * THE BUG THIS EXISTS FOR (reported on a live 1:1, 2026-08-02): a student's
 * shared screen turned black for the tutor at random, and only a page refresh
 * or the student restarting the share brought it back.
 *
 * The chain, read out of livekit-client:
 *
 *  1. `adaptiveStream` is on (room-options.ts) and its `pauseVideoInBackground`
 *     defaults to TRUE, so `RemoteVideoTrack.updateVisibility()` computes
 *     `isVisible = elementInfos.some(i => i.visible) && !backgroundPause`.
 *     Hide the tab for 5s (BACKGROUND_REACTION_DELAY) — a tutor glancing at the
 *     lesson plan — and the share is disabled server-side exactly like a camera.
 *  2. Un-pausing invalidates the forwarder's layer lock, so the SFU must
 *     re-acquire on a KEYFRAME. A screen share of a static worksheet publishes
 *     with contentHint 'text' and maintain-resolution: it emits almost no
 *     spontaneous I-frames. The SFU does send PLIs, but with `dynacast` the
 *     publisher has meanwhile stopped encoding the layer nobody wanted, so the
 *     request races a stopped encoder. A camera hides this because motion keeps
 *     keyframes coming; a static slide can sit black indefinitely.
 *
 * THE FIX is a per-track opt-out rather than a room-wide one. `setEnabled()` is
 * NOT blocked by adaptive stream — `isManualOperationAllowed()` only requires
 * the track to be desired — and once called it wins permanently:
 *
 *     get isEnabled() {
 *       return this.requestedDisabled !== undefined
 *         ? !this.requestedDisabled
 *         : this.isAdaptiveStream ? this.visible : true
 *     }
 *
 * So `setEnabled(true)` on the SHARE pins it on, while every camera keeps the
 * full adaptive behaviour. Deliberately narrower than setting
 * `pauseVideoInBackground: false` room-wide: that would keep up to five cameras
 * flowing to a tab nobody is looking at, which is the bandwidth this app spends
 * real effort saving. The share is the lesson; the cameras are not.
 *
 * COST, stated honestly: while a share is up, the viewer keeps paying for it
 * even with the tab hidden or the whiteboard covering it. That is the trade —
 * one stream, against a lesson that is a black rectangle until someone refreshes.
 *
 * The data saver never unsubscribes or pauses the share (useDataSaver.ts) —
 * the two agree: the share is always desired. At L2 the saver may CAP the
 * subscription to the MEDIUM simulcast layer (LK-027, dataSaver.shareVideoQuality);
 * that is a layer downgrade on a live subscription and does not conflict with
 * this pin — the share stays desired and enabled, just on a lower rung.
 *
 * The pin does not change the requested SIZE, and neither does the saver's
 * HIGH: the SDK serves min(cap, adaptive size), and adaptive size is the
 * share's CSS box. See the request-size effect below.
 */

/** Poll cadence for the watchdog. Cheap: one getStats per share. */
const STALL_POLL_MS = 2000
/**
 * How long a subscribed, on-screen share may decode NOTHING AT ALL before we
 * treat it as never having started.
 *
 * The signal is `framesDecoded === 0`, never "framesDecoded stopped rising".
 * That distinction is the whole safety of this watchdog. The content here is
 * static worksheets and code: a perfectly healthy share can hold one frame on
 * screen and decode nothing for minutes, so a flat counter is the NORMAL case
 * for this product, not a fault. Bouncing on it would glitch every quiet
 * lesson — worse, and more often, than the bug this file exists for. A share
 * that has decoded zero frames since subscribing, while attached to a visible
 * element, has never put a pixel on screen and cannot be a false positive.
 */
const NEVER_STARTED_AFTER_MS = 10000
/** One recovery per share per this window — never thrash a working lesson. */
const RECOVERY_COOLDOWN_MS = 30000
/** Long enough for the server to act on the unsubscribe before we re-ask. */
const RESUBSCRIBE_GAP_MS = 400
/** How long after a bounce we wait before calling it recovered or not. */
const RECOVERY_VERDICT_MS = 6000

type Credential = { code?: string | null; reconnectToken?: string | null }

function remoteSharePublications(room: ReturnType<typeof useRoomContext>): RemoteTrackPublication[] {
  const found: RemoteTrackPublication[] = []
  room?.remoteParticipants.forEach((participant: RemoteParticipant) => {
    participant.videoTrackPublications.forEach((pub) => {
      // `!pub.isMuted` matters as much as the source here. A share a tutor
      // stopped through moderation stays PUBLISHED and muted — LiveKit has no
      // server-side unpublish for someone else's track — and it will never
      // produce another frame. Without this, the never-started watchdog below
      // adopts that corpse: it sees a subscribed share with no pixels, reports
      // `share_never_started` against a share nobody is watching, and bounces
      // the subscription every cooldown for the rest of the lesson. False stall
      // telemetry is worse than none, because it is the metric we would use to
      // judge whether real shares are healthy.
      if (pub.source === Track.Source.ScreenShare && !pub.isMuted) {
        found.push(pub as RemoteTrackPublication)
      }
    })
  })
  return found
}

export function useScreenSharePin(credential: Credential): void {
  const room = useRoomContext()

  // ---- the pin ------------------------------------------------------------
  useEffect(() => {
    if (!room) return
    const pinned = new Set<string>()

    const pin = () => {
      for (const pub of remoteSharePublications(room)) {
        // isDesired, not isSubscribed: setEnabled refuses on an undesired
        // publication, and a just-published share is not subscribed yet.
        if (!pub.isDesired) continue
        pub.setEnabled(true)
        const sid = pub.trackSid
        if (sid && !pinned.has(sid)) {
          pinned.add(sid)
          report(credential, { stage: 'permissions', permission: 'share=pin' })
        }
      }
    }

    pin()
    room.on(RoomEvent.TrackPublished, pin)
    room.on(RoomEvent.TrackSubscribed, pin)
    // A full reconnect rebuilds subscriptions server-side, so the pin has to be
    // re-asserted or the share silently reverts to adaptive control.
    room.on(RoomEvent.Reconnected, pin)
    room.on(RoomEvent.ParticipantConnected, pin)
    return () => {
      room.off(RoomEvent.TrackPublished, pin)
      room.off(RoomEvent.TrackSubscribed, pin)
      room.off(RoomEvent.Reconnected, pin)
      room.off(RoomEvent.ParticipantConnected, pin)
    }
  }, [room, credential])

  // ---- the request size ---------------------------------------------------
  //
  // adaptiveStream asks for the largest registered element's size; the share's
  // stage box is usually < 400 CSS px tall in the presenting layout, which the
  // SFU answers with the 360p@3fps floor. An extra element-info reporting the
  // published size makes the saver's setVideoQuality cap and the SFU's
  // bandwidth allocator the only things choosing the layer. Shares only:
  // cameras keep sizing to their tiles.
  useEffect(() => {
    if (!room) return
    const requests = new Map<RemoteVideoTrack, ElementInfo>()

    const request = (track: RemoteTrack, pub: RemoteTrackPublication) => {
      if (pub.source !== Track.Source.ScreenShare || track.kind !== Track.Kind.Video) return
      const video = track as RemoteVideoTrack
      // @internal in livekit-client (screenShare.test.ts fails if it goes):
      // without it the share just falls back to its box size.
      if (requests.has(video) || typeof video.observeElementInfo !== 'function') return
      const info: ElementInfo = {
        element: {},
        width: () => shareRequestSize(pub.dimensions).width,
        height: () => shareRequestSize(pub.dimensions).height,
        visible: true,
        pictureInPicture: false,
        visibilityChangedAt: undefined,
        observe: () => {},
        stopObserving: () => {},
      }
      requests.set(video, info)
      video.observeElementInfo(info)
    }
    const release = (track: RemoteTrack) => {
      const video = track as RemoteVideoTrack
      const info = requests.get(video)
      if (!info) return
      requests.delete(video)
      video.stopObservingElementInfo?.(info)
    }

    room.remoteParticipants.forEach((participant: RemoteParticipant) => {
      participant.videoTrackPublications.forEach((pub) => {
        if (pub.track) request(pub.track, pub)
      })
    })
    room.on(RoomEvent.TrackSubscribed, request)
    room.on(RoomEvent.TrackUnsubscribed, release)
    return () => {
      room.off(RoomEvent.TrackSubscribed, request)
      room.off(RoomEvent.TrackUnsubscribed, release)
      requests.forEach((info, video) => video.stopObservingElementInfo?.(info))
      requests.clear()
    }
  }, [room])

  // ---- the never-started watchdog -----------------------------------------
  //
  // The pin removes the cause we found. This is the safety net for anything we
  // did not: a dynacast race, a decoder that never latches, an SFU-side pause.
  //
  // It fires ONLY on a share that has never decoded a single frame while
  // attached to a visible element — see NEVER_STARTED_AFTER_MS for why the
  // obvious "frames stopped rising" test is the wrong one for this product.
  // Everything else is reported and left alone: a wrong recovery glitches a
  // lesson that was fine, which is a worse trade than a rare missed stall.
  useEffect(() => {
    if (!room) return
    /** trackSid → when we first saw it subscribed, attached and at zero frames. */
    const blankSince = new Map<string, number>()
    /**
     * Shares we actually bounced. Recovery telemetry is keyed on THIS, not on
     * "was blank and now isn't": every share is briefly at zero frames before
     * its first paint, so crediting that as a recovery would report a success
     * for every healthy start and make the metric useless for judging whether
     * the fix works.
     */
    const bounced = new Set<string>()
    const lastRecovery = new Map<string, number>()
    /** Bounces awaiting their re-subscribe, so teardown can finish them. */
    const inFlight = new Map<number, RemoteTrackPublication>()
    let cancelled = false
    // Re-entrancy guard for tick(): see the guard inside tick.
    let ticking = false

    const tick = async () => {
      // A hidden tab decodes nothing by definition — judging a share there
      // would be diagnosing the browser doing its job.
      if (document.visibilityState !== 'visible') {
        blankSince.clear()
        return
      }
      // Re-entrancy guard: getReceiverStats() awaits per share, and on a slow
      // device a tick can outlast the STALL_POLL_MS cadence. Overlapping ticks
      // would issue concurrent getStats calls and race the per-sid recovery
      // bookkeeping (the cooldown catches the double-bounce, but not the wasted
      // work). Skip a tick that is still in flight.
      if (ticking) return
      ticking = true

      for (const pub of remoteSharePublications(room)) {
        const sid = pub.trackSid
        const track = pub.videoTrack
        // `in` rather than a cast: videoTrack types as local|remote, and only
        // the remote half carries receiver stats.
        if (!sid || !track || !pub.isSubscribed || !('getReceiverStats' in track)) {
          if (sid) blankSince.delete(sid)
          continue
        }
        // Not on screen (the whiteboard is covering the stage, say) — nothing
        // is expected to be decoding, so there is nothing to judge.
        if (track.attachedElements.length === 0) {
          blankSince.delete(sid)
          continue
        }

        let frames: number | undefined
        try {
          const stats = await track.getReceiverStats()
          frames = stats?.framesDecoded
        } catch {
          continue // stats are best-effort; never let telemetry break a class
        }
        if (cancelled || frames === undefined) continue

        // Anything ever decoded means the share HAS painted. Whether the
        // counter is still climbing is not our business: a static worksheet
        // sits at a constant count and looks perfect.
        if (frames > 0) {
          blankSince.delete(sid)
          if (bounced.delete(sid)) {
            report(credential, { stage: 'permissions', permission: 'share=recover-ok' })
          }
          continue
        }

        const now = Date.now()
        const since = blankSince.get(sid)
        if (since === undefined) {
          blankSince.set(sid, now)
          continue
        }
        if (now - since < NEVER_STARTED_AFTER_MS) continue

        const recoveredAt = lastRecovery.get(sid) ?? 0
        if (now - recoveredAt < RECOVERY_COOLDOWN_MS) continue
        lastRecovery.set(sid, now)

        report(credential, {
          stage: 'permissions',
          outcome: 'error',
          errorKind: 'share_never_started',
          permission: `share=stall,enabled=${pub.isEnabled},state=${track.streamState}`,
        })

        // Bounce the subscription: a fresh subscribe is the path that reliably
        // produces a keyframe, which an un-pause alone does not.
        //
        // The re-subscribe is DELAYED rather than called on the next line. Both
        // calls only set desire and emit an update; issued back to back they
        // can be observed by the server as a single net "still subscribed" and
        // the bounce becomes a no-op — the one thing this watchdog must not be.
        try {
          pub.setSubscribed(false)
          bounced.add(sid)
          report(credential, { stage: 'permissions', permission: 'share=recover-resub' })
          const resubscribe = window.setTimeout(() => {
            inFlight.delete(resubscribe)
            pub.setSubscribed(true)
            pub.setEnabled(true) // re-assert the pin on the new subscription
          }, RESUBSCRIBE_GAP_MS)
          inFlight.set(resubscribe, pub)
          // Verdict for the field: did the bounce actually put pixels up?
          window.setTimeout(() => {
            if (cancelled) return
            // Still in `bounced` means no frame arrived since — the recovery
            // did not take.
            if (bounced.has(sid)) {
              report(credential, {
                stage: 'permissions',
                outcome: 'error',
                errorKind: 'share_recover_failed',
                permission: 'share=recover-fail',
              })
            }
          }, RECOVERY_VERDICT_MS)
        } catch {
          /* nothing else to try from here */
        }
      }
      ticking = false
    }

    const timer = window.setInterval(() => void tick(), STALL_POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      // Finish any bounce we started. Clearing these timers alone would leave
      // the share explicitly UNsubscribed — this effect re-runs whenever the
      // credential identity changes, so a teardown mid-bounce could otherwise
      // switch the lesson's screen share off and never switch it back.
      inFlight.forEach((pub, id) => {
        window.clearTimeout(id)
        try {
          pub.setSubscribed(true)
          pub.setEnabled(true)
        } catch {
          /* room is going away anyway */
        }
      })
      inFlight.clear()
    }
  }, [room, credential])
}
